import { useState, useEffect, useCallback, useRef } from 'react'
import { InstrumentRenderer } from './InstrumentRenderer'
import { api } from '../services/api'

interface Task {
  instruction: string
  time_limit_seconds: number
  success_criteria?: string
}

interface Trial {
  id: number
  title: string
  tasks: Task[]
  instrument_id: number | null
  instrument: {
    id: number
    name: string
    schema: any
  } | null
  instrument_timing: 'per_task' | 'post_session'
}

interface TaskTrialProps {
  trial: Trial
  sessionId: string
  onComplete: () => void
}

type Phase = 'briefing' | 'active' | 'instrument' | 'complete'

export function TaskTrial({ trial, sessionId, onComplete }: TaskTrialProps) {
  const [taskIndex, setTaskIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('briefing')
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [taskStartedAt, setTaskStartedAt] = useState<Date | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const currentTask = trial.tasks[taskIndex]
  const isLastTask = taskIndex >= trial.tasks.length - 1

  // Listen for postMessage from app iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'rtgs:taskComplete') {
        handleTaskComplete('postmessage')
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [taskIndex, phase])

  // Timer countdown
  useEffect(() => {
    if (phase === 'active' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleTaskComplete('timeout')
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => {
        if (timerRef.current) clearInterval(timerRef.current)
      }
    }
  }, [phase, timeRemaining > 0])

  const startTask = () => {
    const now = new Date()
    setTaskStartedAt(now)
    setTimeRemaining(currentTask.time_limit_seconds)
    setPhase('active')
  }

  const handleTaskComplete = useCallback(async (method: 'manual' | 'postmessage' | 'timeout') => {
    if (phase !== 'active') return
    if (timerRef.current) clearInterval(timerRef.current)

    const endedAt = new Date()
    const duration = taskStartedAt
      ? (endedAt.getTime() - taskStartedAt.getTime()) / 1000
      : 0

    // Submit result
    try {
      await api.post(`/trials/${trial.id}/results`, {
        task_index: taskIndex,
        session_id: sessionId,
        started_at: taskStartedAt?.toISOString(),
        ended_at: endedAt.toISOString(),
        duration_seconds: duration,
        completed: method !== 'timeout',
        completion_method: method,
      })
    } catch (err) {
      console.error('Failed to submit trial result:', err)
    }

    // Decide next phase
    if (trial.instrument && trial.instrument_timing === 'per_task') {
      setPhase('instrument')
    } else if (isLastTask) {
      if (trial.instrument && trial.instrument_timing === 'post_session') {
        setPhase('instrument')
      } else {
        setPhase('complete')
        onComplete()
      }
    } else {
      advanceToNextTask()
    }
  }, [phase, taskIndex, taskStartedAt, trial, sessionId, isLastTask])

  const advanceToNextTask = () => {
    if (isLastTask) {
      if (trial.instrument && trial.instrument_timing === 'post_session') {
        setPhase('instrument')
      } else {
        setPhase('complete')
        onComplete()
      }
    } else {
      setTaskIndex(prev => prev + 1)
      setPhase('briefing')
    }
  }

  const handleInstrumentSubmit = async (responses: Record<string, number | string>) => {
    // Submit instrument scores for the current task
    try {
      await api.post(`/trials/${trial.id}/results`, {
        task_index: taskIndex,
        session_id: sessionId,
        started_at: taskStartedAt?.toISOString(),
        ended_at: new Date().toISOString(),
        completed: true,
        completion_method: 'manual',
        instrument_scores: responses,
        instrument_raw: responses,
      })
    } catch (err) {
      console.error('Failed to submit instrument scores:', err)
    }

    if (isLastTask || trial.instrument_timing === 'post_session') {
      setPhase('complete')
      onComplete()
    } else {
      advanceToNextTask()
    }
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  // Briefing modal
  if (phase === 'briefing') {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="card max-w-lg w-full mx-4">
          <div className="text-xs text-[var(--color-text-tertiary)] uppercase tracking-wider mb-2">
            Task {taskIndex + 1} of {trial.tasks.length}
          </div>
          <h2 className="text-xl font-medium mb-4">{trial.title}</h2>
          <div className="bg-[var(--color-surface)] rounded p-4 mb-4">
            <p className="text-sm">{currentTask.instruction}</p>
            {currentTask.success_criteria && (
              <p className="text-xs text-[var(--color-text-secondary)] mt-2">
                Success criteria: {currentTask.success_criteria}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--color-text-secondary)]">
              Time limit: {formatTime(currentTask.time_limit_seconds)}
            </span>
            <button onClick={startTask} className="btn btn-primary">
              Begin Task
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Active task — timer overlay
  if (phase === 'active') {
    const urgency = timeRemaining < 30 ? 'text-red-400' : timeRemaining < 60 ? 'text-yellow-400' : ''
    return (
      <div className="fixed top-4 right-4 z-[200] flex items-center gap-3">
        <div className="bg-[var(--color-bg)]/90 backdrop-blur-sm border border-[var(--color-border)] rounded-lg px-4 py-2 flex items-center gap-4">
          <div className="text-xs text-[var(--color-text-secondary)]">
            Task {taskIndex + 1}/{trial.tasks.length}
          </div>
          <div className={`font-mono text-lg ${urgency}`}>
            {formatTime(timeRemaining)}
          </div>
          <button
            onClick={() => handleTaskComplete('manual')}
            className="btn btn-primary text-sm"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  // Instrument modal
  if (phase === 'instrument' && trial.instrument) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="card max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <h2 className="text-lg font-medium mb-4">{trial.instrument.name}</h2>
          <InstrumentRenderer
            schema={trial.instrument.schema}
            onSubmit={handleInstrumentSubmit}
          />
        </div>
      </div>
    )
  }

  // Complete
  return null
}
